/**
 * Token Sizing Terpusat — Control Components (Booku V2)
 *
 * File ini adalah **single source of truth** untuk dimensi (tinggi, padding,
 * font, density) komponen form di Booku V2: textbox (Input/InputNumber),
 * Select, DatePicker, Radio, Checkbox, Switch, Upload, dll.
 *
 * Filosofi:
 * - Semua komponen wrapper di `components/common/inputs/` dan
 *   `components/common/controls/` membaca dari sini.
 * - Mau ubah desain global (mis. perbesar tinggi field jadi 32px)?
 *   Cukup ubah angka di sini → seluruh aplikasi update otomatis.
 * - **DILARANG** hardcode `controlHeight`/`size` di file modul.
 *
 * Konteks pemakaian:
 * - `page`  → filter di halaman list (compact, density tinggi)
 * - `form`  → form input modal (default, density standar)
 * - `drawer` → form di dalam drawer (alias `form` saat ini)
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/control-system-pattern.md`
 */

import type { InputProps } from 'antd';

/**
 * Konteks pemakaian komponen form.
 *
 * - `page` → halaman list (filter), compact density
 * - `form` → form input modal (default)
 * - `drawer` → form dalam drawer (alias `form`)
 * - `auth` → halaman fokus single-purpose seperti login/signup/forgot-password,
 *   density lebih besar (large) supaya proporsional dengan card kosong di sekitarnya
 */
export type ControlContext = 'page' | 'form' | 'drawer' | 'auth';

/**
 * Mapping konteks → Antd `size` prop.
 *
 * Antd component config (`controlHeight`, `controlHeightSM`, `controlHeightLG`)
 * sudah disetel di `antdTheme.ts`. Mapping ini menentukan komponen pakai size
 * mana sesuai konteks.
 */
export function controlSizeForContext(
  context: ControlContext
): InputProps['size'] {
  switch (context) {
    case 'page':
      return 'small'; // 22px (controlHeightSM)
    case 'form':
    case 'drawer':
      return 'middle'; // 28px (controlHeight)
    case 'auth':
      return 'large'; // 36px (controlHeightLG)
  }
}

/**
 * Tinggi kontrol dalam pixel (referensi — sinkron dengan `antdTheme.ts`).
 *
 * Dipakai oleh komponen yang butuh menyamakan tinggi secara eksplisit
 * (mis. tombol di Space.Compact yang harus match tinggi Input di sebelahnya).
 */
export const CONTROL_HEIGHT_PX: Record<ControlContext, number> = {
  page: 22,
  form: 28,
  drawer: 28,
  auth: 36,
};

/**
 * Padding horizontal dalam pixel — referensi untuk align tombol/icon dengan
 * Input di Space.Compact.
 */
export const CONTROL_PADDING_INLINE_PX: Record<ControlContext, number> = {
  page: 6,
  form: 8,
  drawer: 8,
  auth: 11,
};

/**
 * Class CSS yang **wajib** dipasang di semua komponen wrapper family.
 *
 * Targeted oleh `styles/controls.css` untuk menyamakan vertical alignment,
 * line-height, dan inner padding antar komponen yang dirender Antd dengan
 * struktur DOM berbeda (mis. Select pakai `.ant-select-selector`, Input pakai
 * `.ant-input` langsung — tinggi inner element kadang tidak match).
 */
export const CONTROL_CLASS = 'booku-control';
